import React, { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink, ArrowRight, Zap, TrendingUp, Users, Award } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { isEnglishPath } from '../../utils/i18nRoutes';

export default function SuccessfulPilotsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const enRoutes = isEnglishPath(pathname);
  const [hoveredPilot, setHoveredPilot] = useState(null);

  // Parallax effect for background
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  // Hardcoded translations (temporary)
  const texts = {
    en: {
      tag: 'Successful Pilots',
      h1a: 'From idea to',
      h1b: 'impact',
      sub: 'Real deployments solving real problems. These pilots demonstrate how innovation translates into measurable operational improvements.',
      visitApp: 'Visit App →',
      viewProject: 'View Project',
      bottomCta: 'Have an innovative idea for waste or environmental services?',
      contactButton: 'Let\'s Talk',
    },
    es: {
      tag: 'Pilotos Exitosos',
      h1a: 'De la idea al',
      h1b: 'impacto',
      sub: 'Despliegues reales resolviendo problemas reales. Estos pilotos demuestran cómo la innovación se traduce en mejoras operativas medibles.',
      visitApp: 'Visitar App →',
      viewProject: 'Ver proyecto',
      bottomCta: '¿Tienes una idea innovadora para residuos o servicios ambientales?',
      contactButton: 'Hablemos',
    },
  };

  const t = texts[lang] || texts.en;

  const pilots = [
    {
      id: 'tristrash',
      name: 'TrisTrash',
      tagline: lang === 'en' ? 'Smart Waste Operations' : 'Operaciones Inteligentes',
      desc: lang === 'en' 
        ? 'AI-powered platform for urban cleaning operations. Real-time detection management and intelligent fleet assignment.'
        : 'Plataforma con IA para operaciones de limpieza urbana. Gestión de detecciones en tiempo real y asignación inteligente de flota.',
      impact: [
        { icon: Zap, value: '40%', label: lang === 'en' ? 'Faster Response' : 'Respuesta Rápida' },
        { icon: TrendingUp, value: '25%', label: lang === 'en' ? 'Cost Reduction' : 'Reducción Costes' },
        { icon: Users, value: '+200', label: lang === 'en' ? 'Daily Users' : 'Usuarios Diarios' },
      ],
      tech: ['React', 'FastAPI', 'Computer Vision', 'Azure'],
      color: '#3b82f6',
      // YouTube Short video with autoplay and loop
      video: 'https://www.youtube.com/embed/jbneDkbXazY?autoplay=1&mute=1&loop=1&playlist=jbneDkbXazY&controls=0&showinfo=0&modestbranding=1&rel=0&enablejsapi=1&iv_load_policy=3',
      url: enRoutes ? '/en/tristrash' : '/tristrash',
      internal: true,
      gradient: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.05) 100%)',
    },
    {
      id: 'lixo',
      name: 'Lixo',
      tagline: lang === 'en' ? 'Waste Intelligence' : 'Inteligencia Residuos',
      desc: lang === 'en'
        ? 'IoT sensors + ML for smart waste management. Real-time monitoring and predictive collection optimization.'
        : 'Sensores IoT + ML para gestión inteligente de residuos. Monitorización en tiempo real y optimización predictiva de recogida.',
      impact: [
        { icon: Award, value: '1st', label: lang === 'en' ? 'Pilot Winner' : 'Ganador Piloto' },
        { icon: TrendingUp, value: '3x', label: lang === 'en' ? 'Route Efficiency' : 'Eficiencia Rutas' },
        { icon: Zap, value: 'AI', label: lang === 'en' ? 'Powered' : 'IA Integrada' },
      ],
      tech: ['IoT', 'Machine Learning', 'Real-time Analytics'],
      color: '#8DC21F',
      // YouTube video from 0:41 to 0:58 with loop workaround
      video: 'https://www.youtube.com/embed/eNtHBND890c?autoplay=1&mute=1&start=41&end=58&loop=1&playlist=eNtHBND890c&controls=0&showinfo=0&modestbranding=1&rel=0&enablejsapi=1&iv_load_policy=3',
      url: enRoutes ? '/en/lixo' : '/lixo',
      internal: true,
      gradient: 'linear-gradient(135deg, rgba(141,194,31,0.15) 0%, rgba(90,140,26,0.05) 100%)',
    },
  ];

  return (
    <section ref={ref} className="relative py-20 sm:py-28 overflow-hidden">
      {/* Animated background gradient */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #8DC21F 0%, transparent 70%)' }} />
      </motion.div>

      <div className="v-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="v-tag green mb-6 inline-flex">{t.tag}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight">
            {t.h1a}{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>
              {t.h1b}
            </span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 max-w-3xl mx-auto leading-relaxed">
            {t.sub}
          </p>
        </motion.div>

        {/* Pilots grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {pilots.map((pilot, i) => (
            <motion.div
              key={pilot.id}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: i * 0.2 }}
              onMouseEnter={() => setHoveredPilot(pilot.id)}
              onMouseLeave={() => setHoveredPilot(null)}
              className="group liquid-glass rounded-3xl overflow-hidden"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              {/* Visual header */}
              <div className="relative h-56 overflow-hidden" style={{ background: pilot.gradient }}>
                {pilot.video ? (
                  <div className="relative w-full h-full overflow-hidden">
                    <iframe
                      src={pilot.video}
                      className="absolute"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={pilot.name}
                      id={`video-${pilot.id}`}
                      style={{ 
                        border: 'none', 
                        pointerEvents: 'none',
                        width: '177.77%',
                        height: '177.77%',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                      }}
                    />
                    {/* Overlay to prevent interaction */}
                    <div 
                      className="absolute inset-0" 
                      style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent 60%)' }}
                    />
                  </div>
                ) : pilot.gif ? (
                  <img
                    src={pilot.gif}
                    alt={pilot.name}
                    className="w-full h-full object-cover"
                    style={{ filter: 'brightness(0.9)' }}
                  />
                ) : pilot.logo ? (
                  <div className="flex items-center justify-center h-full">
                    <img src={pilot.logo} alt={pilot.name} className="h-20 object-contain" />
                  </div>
                ) : null}
                <div className="absolute bottom-4 left-4 right-4 z-10">
                  <h3 className="text-2xl font-bold text-white mb-1">{pilot.name}</h3>
                  <p className="text-sm font-medium uppercase tracking-wider" style={{ color: pilot.color }}>{pilot.tagline}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-gray-700 dark:text-white/70 leading-relaxed mb-6">{pilot.desc}</p>

                {/* Impact metrics */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {pilot.impact.map((metric, idx) => {
                    const Icon = metric.icon;
                    return (
                      <motion.div
                        key={idx}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={hoveredPilot === pilot.id ? { scale: 1.1, opacity: 1 } : { scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: idx * 0.05 }}
                        className="text-center"
                      >
                        <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: `${pilot.color}15` }}>
                          <Icon size={18} style={{ color: pilot.color }} />
                        </div>
                        <div className="text-xl font-bold mb-1" style={{ color: pilot.color }}>{metric.value}</div>
                        <div className="text-[10px] text-gray-600 dark:text-white/50 uppercase tracking-wide">{metric.label}</div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Tech stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {pilot.tech.map((tech, idx) => (
                    <span
                      key={idx}
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-white/5 dark:bg-white/5 text-gray-600 dark:text-white/60"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                {pilot.internal ? (
                  <Link
                    to={pilot.url}
                    className="inline-flex items-center gap-2 text-sm font-semibold hover:gap-3 transition-all"
                    style={{ color: pilot.color }}
                    data-testid={`pilot-link-${pilot.id}`}
                  >
                    {t.viewProject}
                    <ArrowRight size={14} />
                  </Link>
                ) : pilot.url !== '#' && (
                  <a
                    href={pilot.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold hover:gap-3 transition-all"
                    style={{ color: pilot.color }}
                  >
                    {t.visitApp}
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="text-center"
        >
          <p className="text-sm text-gray-600 dark:text-white/60 mb-4">{t.bottomCta}</p>
          <Link
            to={enRoutes ? '/en/contact' : '/contacto'}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-black transition-all hover:shadow-xl"
            style={{ background: '#8DC21F', boxShadow: '0 6px 20px rgba(141,194,31,0.25)' }}
            data-testid="pilots-bottom-cta-contact"
          >
            {t.contactButton}
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
